import { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  Paper,
  Grid,
} from '@mui/material';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useGetOne, useGetMany, useTranslate, useResourceContext } from 'react-admin';
import type { AuditLog } from './AuditTimeline';

interface AuditDetailsModalProps {
  log: AuditLog | null;
  open: boolean;
  onClose: () => void;
  resourceType?: string; // Optional, will use useResourceContext if not provided
}

const actionColors: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
  create: 'success',
  update: 'info',
  delete: 'error',
  access: 'default',
  attach: 'info',
  detach: 'warning',
};

const actionLabels: Record<string, string> = {
  create: 'Criado',
  update: 'Atualizado',
  delete: 'Excluído',
  access: 'Acessado',
  attach: 'Anexado',
  detach: 'Desanexado',
};

// Helper function for user record representation
const getUserRecordRepresentation = (record: { name?: string; email?: string } | undefined) => {
  if (!record) return null;
  const name = record?.name || "";
  const email = record?.email || "";
  if (!name) {
    return email;
  }
  if (!email) {
    return name;
  }
  return `${name} <${email}>`;
};

// Generate translated description for related changes
const getRelatedChangeDescription = (metadata: Record<string, unknown>, translate: (key: string, options?: any) => string): string => {
  const relatedResourceType = metadata.relatedResourceType as string;
  
  // Status change
  if (relatedResourceType === 'job-application-status' && metadata.statusNameChanged) {
    const oldName = metadata.oldStatusName as string;
    const newName = metadata.newStatusName as string;
    return translate('resources.job-application.audit.relatedChanges.statusRenamed', {
      oldName,
      newName,
    });
  }
  
  // Source change
  if (relatedResourceType === 'job-application-source' && metadata.sourceNameChanged) {
    const oldName = metadata.oldSourceName as string;
    const newName = metadata.newSourceName as string;
    return translate('resources.job-application.audit.relatedChanges.sourceRenamed', {
      oldName,
      newName,
    });
  }
  
  // Company change
  if (relatedResourceType === 'company' && metadata.companyNameChanged) {
    const oldName = metadata.oldCompanyName as string;
    const newName = metadata.newCompanyName as string;
    return translate('resources.job-application.audit.relatedChanges.companyRenamed', {
      oldName,
      newName,
    });
  }
  
  // User change
  if (relatedResourceType === 'user' && metadata.userNameChanged) {
    const oldName = metadata.oldUserName as string;
    const newName = metadata.newUserName as string;
    return translate('resources.job-application.audit.relatedChanges.userRenamed', {
      oldName,
      newName,
    });
  }
  
  // Fallback to changeDescription if available (for backwards compatibility)
  if (metadata.changeDescription) {
    return metadata.changeDescription as string;
  }
  
  return translate('resources.job-application.audit.relatedChanges.generic');
};

const getFieldLabel = (field: string, resourceType: string, translate?: (key: string, options?: any) => string): string => {
  if (!translate) return field;
  
  // Special handling for relatedResource
  if (field === 'relatedResource') {
    return translate(`resources.${resourceType}.audit.relatedChanges.relatedResource`);
  }
  
  // Use resource-specific i18n for field labels with fallback to field name
  return translate(`resources.${resourceType}.fields.${field}`);
};

export const AuditDetailsModal = ({ log, open, onClose, resourceType: propResourceType }: AuditDetailsModalProps) => {
  const translate = useTranslate();
  const contextResourceType = useResourceContext();
  const resourceType = propResourceType || contextResourceType || log?.resourceType || 'resource';
  
  // Always call hooks in the same order, even if log is null
  const { data: user } = useGetOne('user', { id: log?.userId }, { enabled: !!log?.userId });

  // Helper to detect reference resource type from field name
  const getReferenceResourceType = (field: string): string | null => {
    const fieldLower = field.toLowerCase();
    
    // Specific array field patterns
    if (fieldLower === 'roleids') return 'role';
    if (fieldLower === 'groupids') return 'group';
    if (fieldLower === 'userids' || fieldLower === 'groupusersids') return 'user';
    if (fieldLower === 'companyids') return 'company';
    if (fieldLower === 'permissionids') return 'permission';
    
    // Common patterns
    if (fieldLower === 'companyid' || fieldLower.includes('company')) return 'company';
    if (fieldLower === 'userid' || (fieldLower.includes('user') && !fieldLower.includes('group'))) return 'user';
    if (fieldLower === 'statusid' || fieldLower.includes('status')) {
      // Try to infer from resourceType context
      if (resourceType === 'job-application') return 'job-application-status';
      return 'status'; // Generic fallback
    }
    if (fieldLower === 'sourceid' || fieldLower.includes('source')) {
      if (resourceType === 'job-application') return 'job-application-source';
      return 'source'; // Generic fallback
    }
    if (fieldLower.includes('role') && !fieldLower.includes('responsible')) return 'role';
    if (fieldLower.includes('group') && !fieldLower.includes('user')) return 'group';
    if (fieldLower.includes('permission') && !fieldLower.includes('responsible')) return 'permission';
    if (fieldLower.includes('responsible') || fieldLower.includes('supervisor') || fieldLower.includes('assigned')) {
      return 'user';
    }
    
    // Generic: fields ending with Id or Ids might be references
    if ((fieldLower.endsWith('id') || fieldLower.endsWith('ids')) && fieldLower !== 'id' && fieldLower !== 'uuid' && fieldLower !== 'ids') {
      // Try common resource types (handle both singular and plural)
      const baseName = fieldLower.replace(/ids?$/, '');
      if (['company', 'user', 'status', 'source', 'role', 'group', 'permission'].includes(baseName)) {
        return baseName;
      }
    }
    
    return null;
  };

  // Collect all reference IDs that need to be resolved
  const referenceIds = useMemo(() => {
    if (!log) {
      return {} as Record<string, number[]>;
    }

    const ids: Record<string, number[]> = {};
    const changedFields = log.changes?.fields || [];

    if (log.changes?.before && log.changes?.after) {
      changedFields.forEach((field) => {
        const beforeValue = log.changes?.before?.[field];
        const afterValue = log.changes?.after?.[field];
        const refResourceType = getReferenceResourceType(field);

        if (refResourceType) {
          if (!ids[refResourceType]) {
            ids[refResourceType] = [];
          }
          // Handle single number values
          if (beforeValue && typeof beforeValue === 'number') {
            ids[refResourceType].push(beforeValue);
          }
          if (afterValue && typeof afterValue === 'number') {
            ids[refResourceType].push(afterValue);
          }
          // Handle array values (e.g., groupUsersIds, userGroupsIds)
          if (Array.isArray(beforeValue)) {
            beforeValue.forEach((val) => {
              if (typeof val === 'number') {
                ids[refResourceType].push(val);
              }
            });
          }
          if (Array.isArray(afterValue)) {
            afterValue.forEach((val) => {
              if (typeof val === 'number') {
                ids[refResourceType].push(val);
              }
            });
          }
        }
      });
    }

    // Also check metadata for common reference changes
    const metadataKeys = Object.keys(log.metadata || {});
    metadataKeys.forEach((key) => {
      if (key.includes('StatusId') && typeof log.metadata?.[key] === 'number') {
        const refType = resourceType === 'job-application' ? 'job-application-status' : 'status';
        if (!ids[refType]) ids[refType] = [];
        ids[refType].push(log.metadata[key] as number);
      }
      if (key.includes('SourceId') && typeof log.metadata?.[key] === 'number') {
        const refType = resourceType === 'job-application' ? 'job-application-source' : 'source';
        if (!ids[refType]) ids[refType] = [];
        ids[refType].push(log.metadata[key] as number);
      }
      if (key.includes('CompanyId') && typeof log.metadata?.[key] === 'number') {
        if (!ids['company']) ids['company'] = [];
        ids['company'].push(log.metadata[key] as number);
      }
      if (key.includes('UserId') && typeof log.metadata?.[key] === 'number') {
        if (!ids['user']) ids['user'] = [];
        ids['user'].push(log.metadata[key] as number);
      }
      // Handle password change metadata fields
      if ((key === 'changedBy' || key === 'changedForUserId') && typeof log.metadata?.[key] === 'number') {
        if (!ids['user']) ids['user'] = [];
        ids['user'].push(log.metadata[key] as number);
      }
    });

    // Remove duplicates
    Object.keys(ids).forEach((key) => {
      ids[key] = [...new Set(ids[key])];
    });

    return ids;
  }, [log, resourceType]);

  // Fetch references for common resource types (hooks must be called unconditionally)
  const statusIds = referenceIds['job-application-status'] || referenceIds['status'] || [];
  const companyIds = referenceIds['company'] || [];
  const sourceIds = referenceIds['job-application-source'] || referenceIds['source'] || [];
  const userIds = referenceIds['user'] || [];
  const roleIds = referenceIds['role'] || [];
  const groupIds = referenceIds['group'] || [];
  const permissionIds = referenceIds['permission'] || [];

  const statusResourceType = referenceIds['job-application-status'] ? 'job-application-status' : 'status';
  const sourceResourceType = referenceIds['job-application-source'] ? 'job-application-source' : 'source';
  
  const { data: statuses = [] } = useGetMany(
    statusResourceType,
    { ids: statusIds },
    { enabled: statusIds.length > 0 }
  );
  const { data: companies = [] } = useGetMany(
    'company',
    { ids: companyIds },
    { enabled: companyIds.length > 0 }
  );
  const { data: sources = [] } = useGetMany(
    sourceResourceType,
    { ids: sourceIds },
    { enabled: sourceIds.length > 0 }
  );
  const { data: users = [] } = useGetMany(
    'user',
    { ids: userIds },
    { enabled: userIds.length > 0 }
  );
  const { data: roles = [] } = useGetMany(
    'role',
    { ids: roleIds },
    { enabled: roleIds.length > 0 }
  );
  const { data: groups = [] } = useGetMany(
    'group',
    { ids: groupIds },
    { enabled: groupIds.length > 0 }
  );
  const { data: permissions = [] } = useGetMany(
    'permission',
    { ids: permissionIds },
    { enabled: permissionIds.length > 0 }
  );

  // Create lookup maps for all reference types
  const allReferenceMaps = useMemo(() => {
    const maps: Record<string, Map<number, string>> = {};
    
    if (statuses.length > 0) {
      const map = new Map<number, string>();
      statuses.forEach((item: { id: number; name: string }) => {
        map.set(item.id, item.name);
      });
      maps['job-application-status'] = map;
      maps['status'] = map;
    }
    
    if (companies.length > 0) {
      const map = new Map<number, string>();
      companies.forEach((item: { id: number; name: string }) => {
        map.set(item.id, item.name);
      });
      maps['company'] = map;
    }
    
    if (sources.length > 0) {
      const map = new Map<number, string>();
      sources.forEach((item: { id: number; name: string }) => {
        map.set(item.id, item.name);
      });
      maps['job-application-source'] = map;
      maps['source'] = map;
    }
    
    if (users.length > 0) {
      const map = new Map<number, string>();
      users.forEach((user: { id: number; name?: string; email?: string }) => {
        const representation = getUserRecordRepresentation(user);
        if (representation) {
          map.set(user.id, representation);
        }
      });
      maps['user'] = map;
    }
    
    if (roles.length > 0) {
      const map = new Map<number, string>();
      roles.forEach((item: { id: number; name: string }) => {
        map.set(item.id, item.name);
      });
      maps['role'] = map;
    }
    
    if (groups.length > 0) {
      const map = new Map<number, string>();
      groups.forEach((item: { id: number; name: string }) => {
        map.set(item.id, item.name);
      });
      maps['group'] = map;
    }
    
    if (permissions.length > 0) {
      const map = new Map<number, string>();
      permissions.forEach((item: { id: number; code?: string; description?: string; name?: string }) => {
        // Use description, code, or name - prefer description as it's more user-friendly
        const displayName = item.description || item.code || item.name || `ID: ${item.id}`;
        map.set(item.id, displayName);
      });
      maps['permission'] = map;
    }
    
    return maps;
  }, [statuses, companies, sources, users, roles, groups, permissions]);

  // Early return after all hooks are called
  if (!log) return null;

  const actionLabel = actionLabels[log.action] || log.action;
  const actionColor = actionColors[log.action] || 'default';
  const date = format(new Date(log.createdAt), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });

  const hasChanges = log.changes?.before && log.changes?.after;
  const changedFields = log.changes?.fields || [];

  // Function to format value with reference resolution
  // Prefers metadata text (preserved from time of change) over lookup
  const formatValue = (value: unknown, field: string): string => {
    if (value === null || value === undefined) {
      return '(vazio)';
    }
    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }
    
    // Handle array values (e.g., groupUsersIds, userGroupsIds)
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '(nenhum)';
      }
      const refResourceType = getReferenceResourceType(field);
      if (refResourceType && allReferenceMaps[refResourceType]) {
        const resolved = value.map((val) => {
          if (typeof val === 'number') {
            return allReferenceMaps[refResourceType].get(val) || `ID: ${val}`;
          }
          return String(val);
        });
        return resolved.join(', ');
      }
      return value.join(', ');
    }
    
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    // Resolve references - prefer metadata text if available
    if (typeof value === 'number') {
      // Check metadata first (preserved from time of change)
      const metadataKeys = Object.keys(log.metadata || {});
      for (const key of metadataKeys) {
        if (key.includes('Name') && (log.metadata?.[key.replace('Name', 'Id')] === value || 
                                     log.metadata?.[`old${key}`] === value ||
                                     log.metadata?.[`new${key}`] === value)) {
          return log.metadata[key] as string;
        }
      }
      
      // Try to find the reference resource type for this field
      const refResourceType = getReferenceResourceType(field);
      if (refResourceType && allReferenceMaps[refResourceType]) {
        return allReferenceMaps[refResourceType].get(value) || `ID: ${value}`;
      }
      
      // Fallback: try common resource types
      if (allReferenceMaps['user']?.has(value)) {
        return allReferenceMaps['user'].get(value) || `ID: ${value}`;
      }
      if (allReferenceMaps['company']?.has(value)) {
        return allReferenceMaps['company'].get(value) || `ID: ${value}`;
      }
    }

    return String(value);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Chip 
            label={actionLabel} 
            color={actionColor} 
            sx={{ 
              fontWeight: 600,
              fontSize: '0.875rem',
              height: 28,
            }} 
          />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {translate(`resources.${resourceType}.audit.details`)}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 3 }}>
        <Box>
          {/* Basic Info */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              mb: 3, 
              bgcolor: 'background.paper',
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}
                >
                  {translate(`resources.${resourceType}.audit.dateTime`)}
                </Typography>
                <Typography variant="body1" fontWeight={500} sx={{ color: 'text.primary' }}>
                  {date}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}
                >
                  {translate(`resources.${resourceType}.audit.user`)}
                </Typography>
                <Typography variant="body1" fontWeight={500} sx={{ color: 'text.primary' }}>
                  {getUserRecordRepresentation(user) || `ID: ${log.userId}`}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}
                >
                  {translate(`resources.${resourceType}.audit.action`)}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip 
                    label={actionLabel} 
                    color={actionColor} 
                    size="small"
                    sx={{ fontWeight: 500 }}
                  />
                </Box>
              </Grid>
              {log.metadata?.isRelatedChange && (
                <Grid size={12}>
                  <Box sx={{
                    p: 2,
                    bgcolor: 'primary.light',
                    borderRadius: 1.5,
                    border: '2px solid',
                    borderColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Typography variant="body2" sx={{ fontSize: '1.2rem' }}>
                      🔗
                    </Typography>
                    <Typography variant="body2" color="primary.dark" sx={{ fontWeight: 600 }}>
                      {getRelatedChangeDescription(log.metadata, translate)}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Changes Comparison */}
          {hasChanges && changedFields.length > 0 && (
            <>
              <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                  mt: 2, 
                  mb: 2,
                  fontWeight: 600,
                  color: 'text.primary',
                }}
              >
                {translate(`resources.${resourceType}.audit.changes`)}
              </Typography>
              <Box>
                {changedFields.map((field) => {
                  // Special handling for related resource changes
                  if (field === 'relatedResource' && log.metadata?.isRelatedChange) {
                    const relatedResourceType = log.metadata.relatedResourceType as string;
                    let beforeValue: string | null = null;
                    let afterValue: string | null = null;
                    let resourceLabel = translate(`resources.${resourceType}.audit.relatedChanges.resourceLabel`);

                    // Get values from metadata based on resource type
                    // Try to find the field that references this related resource type
                    const changedFields = log.changes?.fields || [];
                    const relatedField = changedFields.find(f => {
                      // Common patterns: {resourceType}Id, {resourceType}Name, etc.
                      return f.toLowerCase().includes(relatedResourceType.toLowerCase().replace(/-/g, ''));
                    });

                    if (relatedField) {
                      // Use the resource-specific field label
                      resourceLabel = translate(`resources.${resourceType}.fields.${relatedField}`);
                    } else {
                      // Fallback to resource type name
                      resourceLabel = translate(`resources.${relatedResourceType}.name`);
                    }

                    // Try to get values from metadata (preserved names) or changes
                    const oldNameKey = `old${relatedResourceType.charAt(0).toUpperCase() + relatedResourceType.slice(1).replace(/-([a-z])/g, (_, c) => c.toUpperCase())}Name`;
                    const newNameKey = `new${relatedResourceType.charAt(0).toUpperCase() + relatedResourceType.slice(1).replace(/-([a-z])/g, (_, c) => c.toUpperCase())}Name`;
                    
                    beforeValue = (log.metadata?.[oldNameKey] || log.changes?.before?.[`${relatedResourceType}Name`]) as string || null;
                    afterValue = (log.metadata?.[newNameKey] || log.changes?.after?.[`${relatedResourceType}Name`]) as string || null;
                    
                    // Fallback to changes object if no names found
                    if (!beforeValue && !afterValue) {
                      beforeValue = log.changes?.before?.[field] as string || null;
                      afterValue = log.changes?.after?.[field] as string || null;
                    }

                    return (
                      <Paper
                        key={field}
                        elevation={0}
                        sx={{
                          p: 3,
                          mb: 2.5,
                          bgcolor: 'background.paper',
                          border: '2px solid',
                          borderColor: 'primary.main',
                          borderRadius: 2,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                          <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'primary.main' }}>
                            🔗 {resourceLabel}
                          </Typography>
                          <Chip
                            label={translate(`resources.${resourceType}.audit.relatedChanges.label`)}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                        <Grid container spacing={3} mt={1}>
                          <Grid size={{ xs: 12, md: 5 }}>
                            <Typography
                              variant="caption"
                              color="error.main"
                              fontWeight={700}
                              sx={{
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: 0.5,
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              {translate(`resources.${resourceType}.audit.before`)}
                            </Typography>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: 'error.light',
                                borderRadius: 1.5,
                                border: '2px solid',
                                borderColor: 'error.main',
                                minHeight: 60,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  m: 0,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontWeight: 500,
                                  color: 'error.dark',
                                }}
                              >
                                {beforeValue || '(vazio)'}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid size={{ xs: 12, md: 2 }} display="flex" alignItems="center" justifyContent="center">
                            <Typography
                              variant="h4"
                              color="primary.main"
                              sx={{ fontWeight: 700 }}
                            >
                              →
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, md: 5 }}>
                            <Typography
                              variant="caption"
                              color="success.main"
                              fontWeight={700}
                              sx={{
                                textTransform: 'uppercase',
                                fontSize: '0.7rem',
                                letterSpacing: 0.5,
                                mb: 1,
                                display: 'block',
                              }}
                            >
                              {translate(`resources.${resourceType}.audit.after`)}
                            </Typography>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: 'success.light',
                                borderRadius: 1.5,
                                border: '2px solid',
                                borderColor: 'success.main',
                                minHeight: 60,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  m: 0,
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontWeight: 500,
                                  color: 'success.dark',
                                }}
                              >
                                {afterValue || '(vazio)'}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  }

                  // Regular field changes
                  const beforeValue = log.changes?.before?.[field];
                  const afterValue = log.changes?.after?.[field];
                  const fieldLabel = translate(`resources.${resourceType}.fields.${field}`);

                  return (
                    <Paper
                      key={field}
                      elevation={0}
                      sx={{
                        p: 3,
                        mb: 2.5,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          borderColor: 'primary.light',
                        },
                      }}
                    >
                      <Typography 
                        variant="subtitle1" 
                        fontWeight={700} 
                        gutterBottom
                        sx={{ color: 'text.primary', mb: 2 }}
                      >
                        {fieldLabel}
                      </Typography>
                      <Grid container spacing={3} mt={1}>
                        <Grid size={{ xs: 12, md: 5 }}>
                          <Typography
                            variant="caption"
                            color="error.main"
                            fontWeight={700}
                            sx={{
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: 0.5,
                              mb: 1,
                              display: 'block',
                            }}
                          >
                            {translate('resources.job-application.audit.before')}
                          </Typography>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: 'error.light',
                              borderRadius: 1.5,
                              border: '2px solid',
                              borderColor: 'error.main',
                              minHeight: 60,
                            }}
                          >
                            <Typography
                              variant="body1"
                              component="pre"
                              sx={{
                                m: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: 'inherit',
                                fontSize: '0.9375rem',
                                color: 'error.dark',
                                fontWeight: 500,
                              }}
                            >
                              {formatValue(beforeValue, field)}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }} display="flex" alignItems="center" justifyContent="center">
                          <Typography
                            variant="h4"
                            color="primary.main"
                            sx={{ fontWeight: 700 }}
                          >
                            →
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                          <Typography
                            variant="caption"
                            color="success.main"
                            fontWeight={700}
                            sx={{
                              textTransform: 'uppercase',
                              fontSize: '0.7rem',
                              letterSpacing: 0.5,
                              mb: 1,
                              display: 'block',
                            }}
                          >
                            {translate('resources.job-application.audit.after')}
                          </Typography>
                          <Box
                            sx={{
                              p: 2,
                              bgcolor: 'success.light',
                              borderRadius: 1.5,
                              border: '2px solid',
                              borderColor: 'success.main',
                              minHeight: 60,
                            }}
                          >
                            <Typography
                              variant="body1"
                              component="pre"
                              sx={{
                                m: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontFamily: 'inherit',
                                fontSize: '0.9375rem',
                                color: 'success.dark',
                                fontWeight: 500,
                              }}
                            >
                              {formatValue(afterValue, field)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  );
                })}
              </Box>
            </>
          )}

          {/* No changes message */}
          {!hasChanges && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: 'background.paper', 
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {translate(`resources.${resourceType}.audit.noChanges`)}
              </Typography>
            </Paper>
          )}

          {/* Metadata */}
          {log.metadata && Object.keys(log.metadata).length > 0 && !log.metadata.isRelatedChange && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography 
                variant="h6" 
                gutterBottom
                sx={{ fontWeight: 600, mb: 2 }}
              >
                {translate(`resources.${resourceType}.audit.additionalInfo`)}
              </Typography>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  bgcolor: 'background.paper', 
                  border: '1px solid', 
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                {log.metadata?.statusChanged && (
                  <Box mb={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {translate(`resources.${resourceType}.audit.metadata.statusChanged`)}
                    </Typography>
                    <Typography variant="body2">
                      {log.metadata.oldStatusName || formatValue(log.metadata.oldStatusId, 'statusId')} → {log.metadata.newStatusName || formatValue(log.metadata.newStatusId, 'statusId')}
                    </Typography>
                  </Box>
                )}
                {log.metadata?.passwordChanged && (
                  <Box mb={1}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {translate(`resources.${resourceType}.audit.metadata.passwordChanged`)}
                    </Typography>
                    <Typography variant="body2">
                      {translate('common.audit.yes')}
                    </Typography>
                  </Box>
                )}
                {Object.entries(log.metadata)
                  .filter(([key]) => 
                    key !== 'isRelatedChange' && 
                    key !== 'changeDescription' && 
                    key !== 'statusChanged' && 
                    key !== 'passwordChanged' &&
                    key !== 'oldStatusId' && key !== 'newStatusId' && 
                    key !== 'oldStatusName' && key !== 'newStatusName' &&
                    key !== 'oldSourceName' && key !== 'newSourceName' &&
                    key !== 'oldSourceId' && key !== 'newSourceId' &&
                    key !== 'oldCompanyName' && key !== 'newCompanyName' &&
                    key !== 'oldCompanyId' && key !== 'newCompanyId' &&
                    key !== 'oldUserName' && key !== 'newUserName' &&
                    key !== 'oldUserEmail' && key !== 'newUserEmail' &&
                    key !== 'relatedResourceType' && key !== 'relatedResourceId' &&
                    key !== 'sourceNameChanged' && key !== 'companyNameChanged' && 
                    key !== 'statusNameChanged' && key !== 'userNameChanged'
                  )
                  .map(([key, value]) => {
                    // Get translated label for metadata keys
                    const translatedKey = translate(`resources.${resourceType}.audit.metadata.${key}`);
                    
                    // Format value based on type and key
                    let formattedValue: string;
                    
                    // Handle boolean values
                    if (typeof value === 'boolean') {
                      formattedValue = value
                        ? translate('common.audit.yes')
                        : translate('common.audit.no');
                    }
                    // Handle user IDs - try to resolve to user name
                    else if ((key === 'changedBy' || key === 'changedForUserId') && typeof value === 'number') {
                      const user = allReferenceMaps['user']?.get(value);
                      formattedValue = user || `ID: ${value}`;
                    }
                    // Handle user names/emails - already formatted
                    else if (key === 'userName' || key === 'userEmail') {
                      formattedValue = String(value);
                    }
                    // Default: convert to string
                    else {
                      formattedValue = String(value);
                    }
                    
                    return (
                      <Box key={key} mb={1}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {translatedKey}
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {formattedValue}
                        </Typography>
                      </Box>
                    );
                  })}
              </Paper>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 2 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          sx={{ minWidth: 100 }}
        >
          {translate(`resources.${resourceType}.audit.close`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

